import React, { ReactChild, FC } from 'react'
import { css } from '@emotion/core'
import { useTheme } from 'emotion-theming'
import SVGIcon from 'components/common/atoms/SVGIcon'
import AlertSVG from 'svgs/alert.svg'

type AlertProps = {
  dataTestId?: string
  onDismiss?: () => void
  isDismissable?: boolean
  variant?: 'success' | 'warning' | 'error' | 'info'
  children: ReactChild
  withIcon?: boolean
}

const alertStyle = (theme: ThemeType, variant: AlertProps['variant'], withIcon: boolean) => {
  const primaryColor = {
    success: theme.success.green40,
    warning: theme.warning.orange40,
    error: theme.error.red40,
    info: theme.info.purple40,
  }
  const secondaryColor = {
    success: theme.success.green10,
    warning: theme.warning.orange10,
    error: theme.error.red20,
    info: theme.info.purple10,
  }
  const variantColor = variant || 'success'
  return css`
    padding: 1rem;
    font-size: ${withIcon ? '14px' : '1rem'};
    font-weight: 600;
    color: ${primaryColor[variantColor]};
    background-color: ${secondaryColor[variantColor]};
    border-radius: 4px;
    position: relative;
    display: flex;
    align-items: center;
    line-height: 1.5;

    .alert-icon {
      margin-right: 12px;
    }
    .close {
      background: none;
      border: none;
      width: 14px;
      height: 14px;
      position: absolute;
      top: 22px;
      right: 16px;
      cursor: pointer;
      span.close-icon {
        &::after,
        &::before {
          content: ' ';
          width: 14px;
          height: 2px;
          background-color: ${primaryColor[variantColor]};
          border-radius: 1px;
          display: block;
          position: absolute;
          top: 6px;
          right: 0;
        }
        &::before {
          transform: rotate(45deg);
        }
        &::after {
          transform: rotate(-45deg);
        }
      }
    }
  `
}

const Alert: FC<AlertProps> = ({ dataTestId, variant, isDismissable, onDismiss, children, withIcon = false }) => {
  const theme: ThemeType = useTheme()

  const handleDismiss = () => {
    onDismiss && onDismiss()
  }

  return (
    <div className="alert" css={alertStyle(theme, variant, withIcon)}>
      {isDismissable && (
        <button className="close" type="button" onClick={handleDismiss} data-testid={dataTestId || ''}>
          <span className="close-icon" />
        </button>
      )}
      {withIcon && (
        <span className="alert-icon">
          <SVGIcon name="alert" size="md" color={theme.error.red40}>
            <AlertSVG />
          </SVGIcon>
        </span>
      )}
      {children}
    </div>
  )
}

export default Alert
